import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from '@/components/AdminLayout';
import { sortStringOptions } from '@/utils/sortOptions';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { toast } from '../utils/toast';
import { Plus, Edit, XCircle, Eye, Mail, Search, Filter, CheckCircle } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EmailTemplatesPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [templates, setTemplates] = useState([]);
  const [filteredTemplates, setFilteredTemplates] = useState([]);
  const [filters, setFilters] = useState({ categories: [], role_types: [] });
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedRoleType, setSelectedRoleType] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  
  useEffect(() => {
    fetchTemplates();
  }, []);
  
  useEffect(() => {
    filterTemplates();
  }, [templates, selectedCategory, selectedRoleType, selectedStatus, searchTerm]);
  
  const fetchTemplates = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/admin/email-templates`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTemplates(response.data.templates);
      setFilters(response.data.filters);
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error('Access denied. Eptral Admin only.');
        navigate('/dashboard');
      } else {
        toast.error('Failed to fetch email templates');
      }
    } finally {
      setLoading(false);
    }
  };
  
  const filterTemplates = () => {
    let filtered = [...templates];
    
    if (selectedCategory !== 'all') {
      filtered = filtered.filter(t => t.category === selectedCategory);
    }
    
    if (selectedRoleType !== 'all') {
      filtered = filtered.filter(t => t.role_type === selectedRoleType);
    }
    
    if (selectedStatus !== 'all') {
      const isActive = selectedStatus === 'active';
      filtered = filtered.filter(t => t.is_active === isActive);
    }
    
    if (searchTerm) {
      filtered = filtered.filter(t => 
        t.template_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.subject.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    setFilteredTemplates(filtered);
  };
  
  const handleDelete = async (templateId, templateName) => {
    if (!window.confirm(`Are you sure you want to delete template "${templateName}"?`)) {
      return;
    }
    
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/admin/email-templates/${templateId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Template deleted successfully');
      fetchTemplates();
    } catch (error) {
      toast.error('Failed to delete template');
    }
  };
  
  const getCategoryLabel = (category) => {
    const labels = {
      signup: 'Sign-Up',
      institution_registration: 'Institution Registration',
      demo_request: 'Demo Request',
      contact_us: 'Contact Us',
      password_reset: 'Password Reset',
      staff_welcome: 'Staff Welcome',
      crm_notification: 'CRM Notification',
      purchase_confirmation: 'Purchase',
      renewal_reminder: 'Renewal',
      expiry_notification: 'Expiry',
      payment_confirmation: 'Payment',
      followup_reminder: 'Follow-up'
    };
    return labels[category] || category;
  };
  
  const getRoleTypeLabel = (roleType) => {
    const labels = {
      hospital_admin: 'Hospital Admin',
      staff: 'Staff',
      crm_officer: 'CRM Officer',
      crm_associate: 'CRM Associate',
      exlva_admin: 'Eptral Admin',
      reporting_manager: 'Reporting Manager'
    };
    return labels[roleType] || roleType;
  };
  
  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-full">
          <div className="w-16 h-16 border-4 border-[#0056B3] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </AdminLayout>
    );
  }
  
  return (
    <AdminLayout>
      <div className="p-8">
        {/* Page Title and Actions */}
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-2xl font-bold text-gray-900">EMAIL TEMPLATE MANAGEMENT</h1>
          <Button 
            onClick={() => navigate('/admin/email-templates/create')}
            className="bg-[#0056B3] hover:bg-[#004494]"
          >
            <Plus className="w-4 h-4 mr-2" />
            Create New Template
          </Button>
        </div>
      
        {/* Statistics - Matching Dashboard Style */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-blue-700 mb-2">Total Templates</p>
                <h3 className="text-4xl font-bold text-blue-900">{templates.length}</h3>
              </div>
              <div className="w-12 h-12 bg-blue-200 rounded-lg flex items-center justify-center">
                <Mail className="w-6 h-6 text-blue-700" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-green-50 to-green-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-green-700 mb-2">Active</p>
                <h3 className="text-4xl font-bold text-green-900">{templates.filter(t => t.is_active).length}</h3>
              </div>
              <div className="w-12 h-12 bg-green-200 rounded-lg flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-green-700" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-orange-700 mb-2">Transactional</p>
                <h3 className="text-4xl font-bold text-orange-900">{templates.filter(t => t.template_type === 'transactional').length}</h3>
              </div>
              <div className="w-12 h-12 bg-orange-200 rounded-lg flex items-center justify-center">
                <Mail className="w-6 h-6 text-orange-700" />
              </div>
            </div>
          </div>
          <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-xl p-6 shadow-lg hover:shadow-xl transition-shadow">
            <div className="flex justify-between items-start">
              <div>
                <p className="text-sm font-medium text-purple-700 mb-2">Notification</p>
                <h3 className="text-4xl font-bold text-purple-900">{templates.filter(t => t.template_type === 'notification').length}</h3>
              </div>
              <div className="w-12 h-12 bg-purple-200 rounded-lg flex items-center justify-center">
                <Mail className="w-6 h-6 text-purple-700" />
              </div>
            </div>
          </div>
        </div>
        
        {/* Filters */}
        <Card className="p-6 mb-6">
          <div className="flex items-center space-x-3 mb-4">
            <Filter className="w-5 h-5 text-[#0056B3]" />
            <h2 className="text-lg font-semibold" style={{ color: '#0056B3' }}>Filters</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <Input
                placeholder="Search templates..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            
            {/* Category Filter */}
            <Select value={selectedCategory} onValueChange={setSelectedCategory}>
              <SelectTrigger>
                <SelectValue placeholder="All Categories" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Categories</SelectItem>
                {sortStringOptions(filters.categories).map(cat => (
                  <SelectItem key={cat} value={cat}>{getCategoryLabel(cat)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Role Type Filter */}
            <Select value={selectedRoleType} onValueChange={setSelectedRoleType}>
              <SelectTrigger>
                <SelectValue placeholder="All Role Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Role Types</SelectItem>
                {sortStringOptions(filters.role_types).map(role => (
                  <SelectItem key={role} value={role}>{getRoleTypeLabel(role)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Status Filter */}
            <Select value={selectedStatus} onValueChange={setSelectedStatus}>
              <SelectTrigger>
                <SelectValue placeholder="All Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Status</SelectItem>
                <SelectItem value="active">Active</SelectItem>
                <SelectItem value="inactive">Inactive</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </Card>
        
        {/* Templates Table */}
        <Card className="p-6">
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Template Name</TableHead>
                  <TableHead>Category</TableHead>
                  <TableHead>Role Type</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Last Updated</TableHead>
                  <TableHead className="text-center">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredTemplates.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                      No email templates found. Create your first template to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredTemplates.map((template) => (
                    <TableRow key={template.template_id}>
                      <TableCell className="font-medium">{template.template_name}</TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                          {getCategoryLabel(template.category)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="bg-purple-50 text-purple-700 border-purple-200">
                          {getRoleTypeLabel(template.role_type)}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        {template.is_active ? (
                          <Badge className="bg-green-100 text-green-800">Active</Badge>
                        ) : (
                          <Badge variant="outline" className="bg-gray-100 text-gray-600">Inactive</Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-sm text-gray-600">
                        {new Date(template.updated_at).toLocaleDateString()}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center justify-center space-x-2">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/admin/email-templates/${template.template_id}/view`)}
                            className="text-blue-600 hover:text-blue-800 hover:bg-blue-50"
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/admin/email-templates/${template.template_id}/edit`)}
                            className="text-[#0056B3] hover:text-[#004494] hover:bg-blue-50"
                          >
                            <Edit className="w-4 h-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleDelete(template.template_id, template.template_name)}
                            className="text-red-600 hover:text-red-800 hover:bg-red-50"
                          >
                            <XCircle className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </Card>
      </div>
    </AdminLayout>
  );
};

export default EmailTemplatesPage;
